import React, { useEffect, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

function KotTimeSlotMaster({ token, selectedProperty }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ kot_timeslot_text: '', from_time: '', to_time: '', is_active: true });
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();

  const chainId = selectedProperty?.chain_id ? String(selectedProperty.chain_id).trim() : '';
  const propertyId = selectedProperty?.id ? String(selectedProperty.id).trim() : '';
  const authHeaders = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': chainId,
    'x-property-id': propertyId,
    ...extra
  });

  useEffect(() => {
    if (!chainId || !propertyId) {
      setRows([]);
      setError('Select a property first from Hotel Config');
      return;
    }
    setError('');
    loadRows();
  }, [token, chainId, propertyId]);

  const loadRows = () => {
    if (!chainId || !propertyId) {
      setRows([]);
      setError('Select a property first from Hotel Config');
      return;
    }
    fetch('/api/v1/kot/masters/time-slots', { headers: authHeaders() })
      .then(async (res) => { const data = await res.json().catch(() => ({})); if (!res.ok) throw new Error(data.error || 'Failed to load KOT Time Slots'); return data; })
      .then(data => setRows(Array.isArray(data) ? data : []))
      .catch((err) => {
        setRows([]);
        setError(err?.message || 'Failed to load KOT Time Slots');
      });
  };

  const handleSave = async () => {
    if (!chainId || !propertyId) {
      setError('Select a property first from Hotel Config');
      return;
    }
    if (!String(form.kot_timeslot_text || '').trim() || !form.from_time || !form.to_time) {
      setError('Time Slot text, From Time and To Time are required');
      return;
    }

    try {
      const res = await fetch(editingId ? `/api/v1/kot/masters/time-slots/${editingId}` : '/api/v1/kot/masters/time-slots', {
        method: editingId ? 'PUT' : 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Save failed');
        return;
      }
      setError('');
      setMessage(editingId ? 'Time slot updated successfully' : 'Time slot created successfully');
      setEditingId(null);
      setForm({ kot_timeslot_text: '', from_time: '', to_time: '', is_active: true });
      loadRows();
    } catch {
      setError('Network error');
    }
  };

  const handleEdit = (row) => {
    const normalize = (value) => String(value || '').slice(0, 5);
    setEditingId(row.id);
    setForm({
      kot_timeslot_text: row.kot_timeslot_text || '',
      from_time: normalize(row.from_time),
      to_time: normalize(row.to_time),
      is_active: row.is_active !== false
    });
    setError('');
    setMessage('');
    scrollToForm();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;
    if (!chainId || !propertyId) {
      setError('Select a property first from Hotel Config');
      return;
    }
    try {
      const res = await fetch(`/api/v1/kot/masters/time-slots/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Delete failed');
        return;
      }
      setError('');
      loadRows();
    } catch {
      setError('Network error');
    }
  };

  const formatTime = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const hhmm = raw.slice(0, 5);
    if (!/^\d{2}:\d{2}$/.test(hhmm)) return raw;
    const [h, m] = hhmm.split(':').map(Number);
    const suffix = h >= 12 ? 'PM' : 'AM';
    const hour12 = (h % 12) || 12;
    return `${hour12}:${String(m).padStart(2, '0')} ${suffix}`;
  };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)' }}>KOT Time Slot</h2>
      {error && <div className="error-banner">{error}</div>}
      {message && <div className="success-banner">{message}</div>}

      <div className="filter-bar" ref={formRef} style={{ marginBottom: '2rem', ...editHighlightStyle(editHighlight) }}>
        <h3>{editingId ? 'Edit Time Slot' : 'Add Time Slot'}</h3>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:'1rem' }}>
          <input
            placeholder="KOT Time Slot (e.g. 6 PM to 11 PM)"
            value={form.kot_timeslot_text}
            onChange={e => setForm({ ...form, kot_timeslot_text: e.target.value })}
            style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1' }}
          />
          <input
            type="time"
            value={form.from_time}
            onChange={e => setForm({ ...form, from_time: e.target.value })}
            style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1' }}
          />
          <input
            type="time"
            value={form.to_time}
            onChange={e => setForm({ ...form, to_time: e.target.value })}
            style={{ padding:'0.75rem', borderRadius:'6px', border:'1px solid #dcdde1' }}
          />
          <label style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={e => setForm({ ...form, is_active: e.target.checked })}
            />
            Active
          </label>
        </div>
        <div style={{ marginTop:'1rem', display:'flex', gap:'0.75rem' }}>
          <button onClick={handleSave} className="btn-primary" style={{ padding:'0.75rem 1.3rem', border:'none', borderRadius:'6px', cursor:'pointer' }}>
            {editingId ? 'Update' : 'Add'}
          </button>
          {editingId && (
            <button onClick={() => { setEditingId(null); setForm({ kot_timeslot_text: '', from_time: '', to_time: '', is_active: true }); setError(''); }} className="btn-muted" style={{ padding:'0.75rem 1.3rem', border:'none', borderRadius:'6px', cursor:'pointer' }}>
              Cancel
            </button>
          )}
        </div>
      </div>

      <h3>KOT Time Slot List</h3>
      {rows.length === 0 ? (
        <p>No KOT Time Slots found</p>
      ) : (
        <table style={{ width:'100%', borderCollapse:'collapse', background:'#fff' }}>
          <thead>
            <tr style={{ background:'var(--theme-primary-dark)', color:'#fff' }}>
              <th style={{ padding:'0.75rem', textAlign:'left' }}>ID</th>
              <th style={{ padding:'0.75rem', textAlign:'left' }}>Time Slot</th>
              <th style={{ padding:'0.75rem', textAlign:'left' }}>From Time</th>
              <th style={{ padding:'0.75rem', textAlign:'left' }}>To Time</th>
              <th style={{ padding:'0.75rem', textAlign:'left' }}>Active</th>
              <th style={{ padding:'0.75rem', textAlign:'left' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.id} style={{ borderBottom:'1px solid #dcdde1' }}>
                <td style={{ padding:'0.75rem' }}>{row.id}</td>
                <td style={{ padding:'0.75rem' }}>{row.kot_timeslot_text}</td>
                <td style={{ padding:'0.75rem' }}>{formatTime(row.from_time)}</td>
                <td style={{ padding:'0.75rem' }}>{formatTime(row.to_time)}</td>
                <td style={{ padding:'0.75rem' }}>{row.is_active ? 'Yes' : 'No'}</td>
                <td style={{ padding:'0.75rem', display:'flex', gap:'0.5rem' }}>
                  <button onClick={() => handleEdit(row)} style={{ border:'none', borderRadius:'4px', padding:'0.45rem 0.8rem', background:'#3498db', color:'#fff', cursor:'pointer' }}>Edit</button>
                  <button onClick={() => handleDelete(row.id)} className="btn-danger" style={{ border:'none', borderRadius:'4px', padding:'0.45rem 0.8rem', cursor:'pointer' }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default KotTimeSlotMaster;
